// src/pages/GpmDeliveries.jsx
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCommands } from '../api/commands';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';

export default function GpmDeliveries(){
  const cmd = useCommands();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [filter, setFilter] = useState('pending');
  const [q, setQ] = useState('');

  // mark-delivered modal
  const [active, setActive] = useState(null);
  const [confirmRef, setConfirmRef] = useState('');
  const [recipient, setRecipient] = useState('');

  // edit modal
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ item: '', amount: 1, unitCost: 0, supplier: '', office: '', orderedBy: '', orderedDate: '' });

  async function load(){
    setLoading(true); setErr(''); setOkMsg('');
    try{
      const data = await cmd.deliveries.list();
      setRows(Array.isArray(data) ? data : []);
    }catch(e){
      setErr(e?.response?.data?.error || e?.message || 'Failed to load deliveries');
    }finally{
      setLoading(false);
    }
  }
  useEffect(()=>{ load(); },[]);// eslint-disable-line react-hooks/exhaustive-deps

  const pending = useMemo(()=>rows.filter(d=>!d.deliveredDate),[rows]);
  const delivered = useMemo(()=>rows.filter(d=>!!d.deliveredDate),[rows]);

  const baseShown = filter === 'pending' ? pending : delivered;
  const shown = useMemo(()=>{
    const term = q.trim().toLowerCase();
    if (!term) return baseShown;
    return baseShown.filter(d => [
      d.item, d.supplier, d.office, d.orderedBy, d.recipient, d.confirmation
    ].some(v => String(v||'').toLowerCase().includes(term)));
  }, [baseShown, q]);

  // ── Mark delivered
  function openDeliverModal(d){
    setActive(d);
    setConfirmRef(d.confirmation || '');
    setRecipient(d.recipient || '');
  }
  function closeDeliverModal(){
    setActive(null);
    setConfirmRef('');
    setRecipient('');
  }

  async function applyInventoryIncrement(site, itemId, amount, unitCost) {
    const inv = await cmd.inventory.get(site);
    const existing = inv.find(
      x => String(x.item || '').toLowerCase() === String(itemId || '').toLowerCase()
    );
    const packOf = existing ? Math.max(1, Number(existing.packOf || 1)) : 1;
    const unitsToAdd = Number(amount || 0) * packOf;

    if (existing) {
      return cmd.inventory.patch(site, {
        ops: [{ type: 'inc', id: itemId, delta: unitsToAdd }],
        reason: `Delivery from GPM (${new Date().toLocaleDateString('en-GB')})`,
        team: 'Deliveries'
      });
    } else {
      return cmd.inventory.patch(site, {
        ops: [{
          type: 'add',
          item: { id: itemId, name: itemId, qty: unitsToAdd, category: '', unitPrice: Number(unitCost || 0) }
        }],
        reason: 'New item from GPM delivery',
        team: 'Deliveries'
      });
    }
  }

  async function markDelivered(){
    if (!active) return;
    setErr('');
    const deliveredDate = new Date().toISOString();
    try{
      await cmd.deliveries.update(active._id, {
        deliveredDate,
        confirmation: confirmRef || '',
        recipient: recipient || ''
      });
      await applyInventoryIncrement('main', active.item, active.amount, active.unitCost);
      await load();
      closeDeliverModal();
      setOkMsg('Marked as delivered and added to inventory.');
    }catch(e){
      setErr(e?.response?.data?.error || e?.message || 'Failed to mark delivered');
    }
  }

  // ── Edit
  function openEditModal(d) {
    setEditTarget(d);
    setEditForm({
      item:        d.item        || '',
      amount:      Number(d.amount) || 1,
      unitCost:    Number(d.unitCost) || 0,
      supplier:    d.supplier    || '',
      office:      d.office      || '',
      orderedBy:   d.orderedBy   || '',
      orderedDate: d.orderedDate ? new Date(d.orderedDate).toISOString().slice(0, 10) : '',
    });
  }
  function closeEditModal(){ setEditTarget(null); }

  async function saveEdit() {
    if (!editTarget) return;
    setErr('');
    try {
      await cmd.deliveries.update(editTarget._id, editForm);
      await load();
      closeEditModal();
      setOkMsg('Order updated.');
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to update delivery');
    }
  }

  // ── Delete
  async function deleteDelivery(d) {
    if (!window.confirm(`Cancel and delete order for "${d.item || 'this item'}"? This cannot be undone.`)) return;
    setErr('');
    try {
      await cmd.deliveries.delete(d._id);
      await load();
      setOkMsg('Order deleted.');
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to delete delivery');
    }
  }

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '—';

  return (
    <div className="container">
      <PageHeader
        eyebrow="GPM"
        title="Orders"
        actions={
          <>
            <Link to="/gpm/inventory" className="btn secondary">Inventory</Link>
            <Link to="/gpm/deliveries/new" className="btn"><Icon name="plus" size={15} /> New order</Link>
          </>
        }
      />

      {(err || okMsg) && (
        <div className="page-alerts">
          {err   && <div className="alert error">{err}</div>}
          {okMsg && <div className="alert success">{okMsg}</div>}
        </div>
      )}

      <div className="card">
        <div className="toolbar mb-16">
          <div className="segmented">
            <button className={filter==='pending'?'seg active':'seg'} onClick={()=>setFilter('pending')}>
              Pending <span className="seg-count">{pending.length}</span>
            </button>
            <button className={filter==='delivered'?'seg active':'seg'} onClick={()=>setFilter('delivered')}>
              Delivered <span className="seg-count">{delivered.length}</span>
            </button>
          </div>
          <input
            className="input grow"
            placeholder="Search items, suppliers, offices"
            value={q}
            onChange={e=>setQ(e.target.value)}
          />
          <button className="btn secondary" onClick={load}>Reload</button>
        </div>

        {loading ? <div className="empty">Loading</div> : shown.length === 0 ? (
          <div className="empty">No {filter} orders.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Qty</th>
                  <th className="num">Unit cost</th>
                  <th className="num">Total</th>
                  <th>Supplier</th>
                  <th>Office</th>
                  <th>Intended for</th>
                  <th>Ordered</th>
                  <th>Delivered</th>
                  <th className="actions"></th>
                </tr>
              </thead>
              <tbody>
                {shown.map(d => (
                  <tr key={d._id}>
                    <td className="cell-strong">{d.item}</td>
                    <td className="num">{Number(d.amount||0).toLocaleString('en-GB')}</td>
                    <td className="num">£{Number(d.unitCost||0).toFixed(2)}</td>
                    <td className="num">£{(Number(d.amount||0) * Number(d.unitCost||0)).toFixed(2)}</td>
                    <td>{d.supplier || '—'}</td>
                    <td>{d.office || 'main'}</td>
                    <td>{d.orderedBy || '—'}</td>
                    <td className="nowrap">{fmtDate(d.orderedDate)}</td>
                    <td className="nowrap">{fmtDate(d.deliveredDate)}</td>
                    <td className="actions">
                      {!d.deliveredDate ? (
                        <div className="row-actions">
                          <button className="btn secondary small" onClick={()=>openEditModal(d)}>Edit</button>
                          <button className="btn secondary small destructive" onClick={()=>deleteDelivery(d)}>Delete</button>
                          <button className="btn small" onClick={()=>openDeliverModal(d)}>Mark delivered</button>
                        </div>
                      ) : (
                        <span className="badge ok">Delivered</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mark delivered modal */}
      {active && (
        <div className="modal-backdrop" onClick={closeDeliverModal}>
          <div className="modal" role="dialog" aria-modal="true" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <h3>Mark as delivered</h3>
              <p className="modal-sub">
                {active.item}, {Number(active.amount||0).toLocaleString('en-GB')} at £{Number(active.unitCost||0).toFixed(2)}
              </p>
            </div>
            <div className="form-grid cols-2">
              <label className="input-label">Delivery note or reference
                <input className="input" value={confirmRef} onChange={e=>setConfirmRef(e.target.value)} />
              </label>
              <label className="input-label">Recipient
                <input className="input" value={recipient} onChange={e=>setRecipient(e.target.value)} />
              </label>
            </div>
            <div className="modal-foot">
              <button className="btn secondary" onClick={closeDeliverModal}>Cancel</button>
              <button className="btn" onClick={markDelivered}>Confirm delivery</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editTarget && (
        <div className="modal-backdrop" onClick={closeEditModal}>
          <div className="modal" role="dialog" aria-modal="true" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <h3>Edit order</h3>
            </div>
            <div className="form-grid cols-2">
              <label className="input-label span-all">Item
                <input className="input" value={editForm.item}
                  onChange={e=>setEditForm(f=>({...f, item: e.target.value}))} />
              </label>
              <label className="input-label">Quantity
                <input type="number" className="input" value={editForm.amount} min={1}
                  onChange={e=>setEditForm(f=>({...f, amount: Number(e.target.value)}))} />
              </label>
              <label className="input-label">Unit cost
                <div className="input-wrap"><span className="prefix">£</span>
                  <input type="number" className="input" value={editForm.unitCost} min={0} step={0.01}
                    onChange={e=>setEditForm(f=>({...f, unitCost: Number(e.target.value)}))} />
                </div>
              </label>
              <label className="input-label">Supplier
                <input className="input" value={editForm.supplier}
                  onChange={e=>setEditForm(f=>({...f, supplier: e.target.value}))} />
              </label>
              <label className="input-label">Office
                <input className="input" value={editForm.office}
                  onChange={e=>setEditForm(f=>({...f, office: e.target.value}))} />
              </label>
              <label className="input-label">Ordered by
                <input className="input" value={editForm.orderedBy}
                  onChange={e=>setEditForm(f=>({...f, orderedBy: e.target.value}))} />
              </label>
              <label className="input-label">Order date
                <input type="date" className="input" value={editForm.orderedDate}
                  onChange={e=>setEditForm(f=>({...f, orderedDate: e.target.value}))} />
              </label>
            </div>
            <div className="modal-foot">
              <button className="btn secondary" onClick={closeEditModal}>Cancel</button>
              <button className="btn" onClick={saveEdit}>Save changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
